import React, { useState, useEffect } from 'react';
import { RefreshCw, Plus, X, Calculator } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// Helper function to get Brazil local date (UTC-3)
const getBrazilDate = () => {
  // CORREÇÃO: Usar a data do navegador do usuário (não do servidor)
  const now = new Date();
  
  // Obter a data local no formato YYYY-MM-DD
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const AddBetForm = () => {
  const { user } = useAuth();
  const [registryType, setRegistryType] = useState('individual');
  const [bettingHouses, setBettingHouses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [syncInfo, setSyncInfo] = useState({
    totalHouses: 0,
    userHouses: 0,
    totalGroups: 0,
    userGroups: 0,
    lastSync: new Date().toLocaleTimeString()
  });

  const [formData, setFormData] = useState({
    bettingHouseId: '',
    groupId: '',
    date: getBrazilDate(),
    gameDateTime: getBrazilDate(), // Apenas data, sem hora
    matchDescription: '',
    betType: 'Aposta Individual',
    odd: '',
    stake: '',
    units: '', // Quantas unidades
    stakeMode: 'value', // 'value' ou 'units'
    result: '', // Sem valor padrão - usuário deve selecionar
    cashoutValue: '',
    comments: '',
    status: 'completed', // completed or pending
    isPending: false
  });

  const [dailyFormData, setDailyFormData] = useState({
    bettingHouseId: '',
    groupId: '',
    date: getBrazilDate(),
    initialAmount: '',
    finalAmount: '',
    comments: ''
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load betting houses
      const housesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`);
      if (housesResponse.ok) {
        const houses = await housesResponse.json();
        setBettingHouses(houses);
      }

      // Load groups
      const groupsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups?user_id=${user.id}`);
      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
      }

      // Update sync info
      setSyncInfo(prev => ({
        ...prev,
        userHouses: bettingHouses.length,
        userGroups: groups.length,
        lastSync: new Date().toLocaleTimeString()
      }));
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const actualValue = type === 'checkbox' ? checked : value;
    
    if (registryType === 'individual') {
      setFormData(prev => ({ ...prev, [name]: actualValue }));
    } else {
      setDailyFormData(prev => ({ ...prev, [name]: actualValue }));
    }
  };

  const handleResultChange = (result) => {
    setFormData(prev => ({ ...prev, result }));
  };

  const handleUnitsChange = (units) => {
    const unitsValue = parseFloat(units) || 0;
    const unitValue = user.unit_value || 0;
    const calculatedStake = unitsValue * unitValue;
    
    setFormData({
      ...formData,
      units: units,
      stake: calculatedStake > 0 ? calculatedStake.toFixed(2) : ''
    });
  };

  const handleStakeModeChange = (mode) => {
    setFormData({
      ...formData,
      stakeMode: mode,
      units: '',
      stake: ''
    });
  };

  const clearForm = () => {
    const currentDate = getBrazilDate();
    if (registryType === 'individual') {
      setFormData({
        bettingHouseId: '',
        groupId: '',
        date: currentDate,
        gameDateTime: currentDate,
        matchDescription: '',
        betType: 'Aposta Individual',
        odd: '',
        stake: '',
        units: '',
        stakeMode: user?.unit_value ? 'units' : 'value',
        result: '', // Sem valor padrão - usuário deve selecionar
        cashoutValue: '',
        comments: '',
        status: 'completed',
        isPending: false
      });
    } else {
      setDailyFormData({
        bettingHouseId: '',
        groupId: '',
        date: currentDate,
        initialAmount: '',
        finalAmount: '',
        comments: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      
      let endpoint, payload;
      
      if (registryType === 'individual') {
        endpoint = '/api/bets';
        
        // Para apostas pendentes, usar apenas a data do jogo (sem hora)
        let gameDateTime = null;
        if (formData.isPending && formData.gameDateTime) {
          // Se é aposta pendente, apenas adicionar uma hora padrão (12:00) à data selecionada
          gameDateTime = `${formData.gameDateTime}T12:00:00`;
        }
        
        payload = {
          betting_house_id: formData.bettingHouseId,
          group_id: formData.groupId,
          date: formData.date,
          game_datetime: gameDateTime,
          match_description: formData.isPending ? formData.matchDescription : null,
          bet_type: formData.betType,
          odd: parseFloat(formData.odd),
          stake: parseFloat(formData.stake),
          units: formData.stakeMode === 'units' && formData.units ? parseFloat(formData.units) : null,
          result: formData.isPending ? 'pending' : formData.result,
          cashout_value: formData.result === 'cashout' && !formData.isPending ? parseFloat(formData.cashoutValue || 0) : null,
          comments: formData.comments,
          status: formData.isPending ? 'pending' : 'completed'
        };
      } else {
        endpoint = '/api/bets/daily-record';
        payload = {
          betting_house_id: dailyFormData.bettingHouseId,
          group_id: dailyFormData.groupId,
          date: dailyFormData.date,
          initial_amount: parseFloat(dailyFormData.initialAmount),
          final_amount: parseFloat(dailyFormData.finalAmount),
          comments: dailyFormData.comments
        };
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}?user_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // Show success notification
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 5000); // Hide after 5 seconds
        clearForm();
      } else {
        throw new Error('Falha ao salvar');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      alert('Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Nova Aposta
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Registre uma nova aposta no sistema
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-colors flex items-center space-x-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Atualizar Dados</span>
        </button>
      </div>

      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-2 z-50 animate-bounce">
          <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
            <span className="text-green-500 text-sm font-bold">✓</span>
          </div>
          <span className="font-medium">
            {registryType === 'individual' ? 'Aposta registrada com sucesso!' : 'Registro diário salvo com sucesso!'}
          </span>
        </div>
      )}

      {/* Sync Status */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">📊 Status de Sincronização</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-600 dark:text-blue-300">Total Casas:</span>
            <span className="ml-2 font-bold">{bettingHouses.length}</span>
          </div>
          <div>
            <span className="text-blue-600 dark:text-blue-300">Minhas Casas:</span>
            <span className="ml-2 font-bold">{bettingHouses.length}</span>
          </div>
          <div>
            <span className="text-blue-600 dark:text-blue-300">Total Grupos:</span>
            <span className="ml-2 font-bold">{groups.length}</span>
          </div>
          <div>
            <span className="text-blue-600 dark:text-blue-300">Meus Grupos:</span>
            <span className="ml-2 font-bold">{groups.length}</span>
          </div>
        </div>
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
          Última sincronização: {syncInfo.lastSync}
        </p>
      </div>

      {/* Registry Type Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tipo de Registro</h3>
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => setRegistryType('individual')}
            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all duration-200 ${
              registryType === 'individual'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Plus className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Registro Individual</div>
                <div className="text-sm opacity-75">Cadastrar cada aposta separadamente</div>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setRegistryType('daily')}
            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all duration-200 ${
              registryType === 'daily'
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <Calculator className="w-5 h-5" />
              <div className="text-left">
                <div className="font-semibold">Registro Diário</div>
                <div className="text-sm opacity-75">Valor inicial vs valor final do dia</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {registryType === 'individual' ? 'Registro Individual de Aposta' : 'Registro Diário de Balanço'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Casa de Apostas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Casa de Apostas *</label>
              <select
                name="bettingHouseId"
                required
                value={registryType === 'individual' ? formData.bettingHouseId : dailyFormData.bettingHouseId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Selecione uma casa de apostas</option>
                {bettingHouses.map(house => (
                  <option key={house.id} value={house.id}>{house.name}</option>
                ))}
              </select>
              {bettingHouses.length === 0 && (
                <p className="text-sm text-red-500 mt-1">❌ Nenhuma casa de apostas cadastrada. Vá em "Gestão de Banca" para adicionar.</p>
              )}
            </div>

            {/* Grupo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Grupo *</label>
              <select
                name="groupId"
                required
                value={registryType === 'individual' ? formData.groupId : dailyFormData.groupId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Selecione um grupo</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
              {groups.length === 0 && (
                <p className="text-sm text-red-500 mt-1">❌ Nenhum grupo cadastrado. Vá em "Grupos" para adicionar.</p>
              )}
            </div>

            {/* Data */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data de Registro *</label>
              <input
                type="date"
                name="date"
                required
                value={registryType === 'individual' ? formData.date : dailyFormData.date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {registryType === 'individual' ? (
              <>
                {/* Toggle Aposta Pendente */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">📌 Tipo de Registro</h4>
                      <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                        Escolha se a aposta já foi finalizada ou ainda está pendente
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${!formData.isPending ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
                        ✅ Finalizada
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="isPending"
                          checked={formData.isPending}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                      <span className={`text-sm font-medium ${formData.isPending ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500'}`}>
                        🕐 Pendente
                      </span>
                    </div>
                  </div>
                  
                  {formData.isPending && (
                    <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                      <p className="text-sm text-orange-800 dark:text-orange-200">
                        <strong>💡 Aposta Pendente:</strong> Você poderá finalizar o resultado mais tarde na seção "Apostas Pendentes"
                      </p>
                    </div>
                  )}
                </div>

                {/* Descrição do Jogo - Obrigatório para apostas pendentes */}
                {formData.isPending && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Descrição do Jogo *
                    </label>
                    <input
                      type="text"
                      name="matchDescription"
                      value={formData.matchDescription}
                      onChange={handleInputChange}
                      required={formData.isPending}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ex: Real Madrid vs Barcelona, Over 2.5 Goals"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Descreva a partida e o tipo de aposta para facilitar a identificação na aba "Apostas Pendentes"
                    </p>
                  </div>
                )}

                {/* Data/Hora do Jogo - Só data para apostas pendentes */}
                {formData.isPending && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data do Jogo *
                    </label>
                    <input
                      type="date"
                      name="gameDateTime"
                      value={formData.gameDateTime}
                      onChange={handleInputChange}
                      required={formData.isPending}
                      min={getBrazilDate()}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Quando o jogo vai acontecer? Isso ajuda a organizar as apostas pendentes
                    </p>
                  </div>
                )}
                {/* Odd */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Odd *</label>
                  <input
                    type="number"
                    name="odd"
                    required
                    step="0.01"
                    min="1.01"
                    placeholder="Ex: 2.50"
                    value={formData.odd}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Stake - Modo inteligente com unidades */}
                <div className="md:col-span-2">
                  {user?.unit_value ? (
                    <>
                      {/* Toggle entre Unidades e Valor */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Modo de Stake
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => handleStakeModeChange('units')}
                            className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${
                              formData.stakeMode === 'units'
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            🎯 Unidades
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStakeModeChange('value')}
                            className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${
                              formData.stakeMode === 'value'
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            💰 Valor (R$)
                          </button>
                        </div>
                      </div>

                      {formData.stakeMode === 'units' ? (
                        <>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Unidades * <span className="text-xs text-gray-500">(1u = R$ {user.unit_value.toFixed(2)})</span>
                          </label>
                          <input
                            type="number"
                            required
                            step="0.01"
                            min="0.01"
                            placeholder="Ex: 1.5"
                            value={formData.units}
                            onChange={(e) => handleUnitsChange(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          {formData.units && parseFloat(formData.units) > 0 && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                              = R$ {(parseFloat(formData.units) * user.unit_value).toFixed(2)}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Stake (R$) *
                          </label>
                          <input
                            type="number"
                            required
                            step="0.01"
                            min="0.01"
                            placeholder="Ex: 100.00"
                            value={formData.stake}
                            onChange={handleInputChange}
                            name="stake"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Stake (R$) *
                      </label>
                      <input
                        type="number"
                        name="stake"
                        required
                        step="0.01"
                        min="0.01"
                        placeholder="Ex: 100.00"
                        value={formData.stake}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </>
                  )}
                </div>

                {/* Result - Só para apostas finalizadas */}
                {!formData.isPending && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resultado *</label>
                    <select
                      name="result"
                      required={!formData.isPending}
                      value={formData.result}
                      onChange={(e) => handleResultChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="" disabled>Selecione o resultado...</option>
                    <option value="green">🟢 Green (Ganhou)</option>
                    <option value="red">🔴 Red (Perdeu)</option>
                    <option value="cashout">🔵 Cashout (Encerrada)</option>
                  </select>
                  </div>
                )}

                {/* Cashout Value - Only show when result is cashout and not pending */}
                {!formData.isPending && formData.result === 'cashout' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Valor do Cashout (R$) *
                    </label>
                    <input
                      type="number"
                      name="cashoutValue"
                      required={formData.result === 'cashout'}
                      step="0.01"
                      min="0"
                      placeholder="Valor que você recebeu no cashout"
                      value={formData.cashoutValue}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Digite o valor que você recebeu ao encerrar a aposta antes do resultado final
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Initial Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Valor Inicial (R$) *</label>
                  <input
                    type="number"
                    name="initialAmount"
                    required
                    step="0.01"
                    min="0"
                    placeholder="Ex: 1000.00"
                    value={dailyFormData.initialAmount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Final Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Valor Final (R$) *</label>
                  <input
                    type="number"
                    name="finalAmount"
                    required
                    step="0.01"
                    min="0"
                    placeholder="Ex: 1200.00"
                    value={dailyFormData.finalAmount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {/* Comments */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Comentários</label>
              <textarea
                name="comments"
                rows="3"
                placeholder={registryType === 'individual' ? 'Observações sobre a aposta (opcional)' : 'Observações sobre o dia (opcional)'}
                value={registryType === 'individual' ? formData.comments : dailyFormData.comments}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={clearForm}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <X className="w-4 h-4" />
              <span>Limpar</span>
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              <span>{loading ? 'Salvando...' : registryType === 'individual' ? 'Salvar Aposta' : 'Salvar Registro'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBetForm;